import {
  KeyOutlined,
  OrderedListOutlined,
  ReloadOutlined,
  SwitcherOutlined,
} from '@ant-design/icons';
import { Alert, Button, Skeleton, Tooltip } from 'antd';
import { AxiosError } from 'axios';
import { motion } from 'motion/react';
import {
  memo,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import InfraGraphIcon from 'images/infra-graph-icon.svg?react';
import CliCommandPalette from './CliCommandPalette';
import CliTerminal, { CliTerminalHandle } from './CliTerminal';
import CliToolPanel from './CliToolPanel';
import {
  noteCliCommandDone,
  noteCliRunning,
  setCliDockMinimized,
  setCliPicking,
  takeCliRequest,
  useCliRequest,
} from './cliSessionStore';
import { useAccess } from 'hooks/useAccess';
import { serializePath } from 'components/InfraMap/graph/pathState';
import { mapPathOfTarget } from 'helpers/cliMapTargets';
import { CliTarget } from 'types/cli';
import { useCliTerminal } from 'hooks/cliTerminalQueries';
import { clusterInfraPath } from 'utils/clusterPaths';
import { centreOf, startPageTransition } from 'utils/pageTransition';

export type CliToolKey = 'tasks' | 'commands';

type ToolMode = 'closed' | 'docked' | 'floating';

interface CliSessionPanelProps {
  clusterName: string;
  active: boolean;
  outOfSight: boolean;
}

const TOOLS: { key: CliToolKey; label: string; icon: ReactNode }[] = [
  { key: 'tasks', label: 'Shortcuts', icon: <OrderedListOutlined /> },
  { key: 'commands', label: 'Commands', icon: <SwitcherOutlined /> },
];

const CLOSED_TOOLS: Record<CliToolKey, ToolMode> = {
  tasks: 'closed',
  commands: 'closed',
};

const toolKeys = Object.keys(CLOSED_TOOLS) as CliToolKey[];
const dockedIn = (tools: Record<CliToolKey, ToolMode>) =>
  toolKeys.find(key => tools[key] === 'docked') ?? null;

const TARGET_SPRING = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 42,
  mass: 0.7,
};

function CliSessionPanel({
  clusterName,
  active,
  outOfSight,
}: Readonly<CliSessionPanelProps>) {
  const cli = useCliTerminal({ clusterName });
  const terminalRefs = useRef(new Map<string, CliTerminalHandle>());
  const request = useCliRequest(clusterName);
  const { can } = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const canOpenMap = can('infrastructure.read');

  const [tools, setTools] =
    useState<Record<CliToolKey, ToolMode>>(CLOSED_TOOLS);
  const [sideHost, setSideHost] = useState<HTMLDivElement | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [runningTargets, setRunningTargets] = useState<Set<string>>(
    () => new Set(),
  );
  const [dropping, setDropping] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);

  const activeTarget: CliTarget | undefined = useMemo(
    () =>
      cli.targets.find(target => target.id === targetId) ??
      cli.targets.find(target => target.id === cli.defaultTargetId) ??
      cli.targets[0],
    [cli.targets, cli.defaultTargetId, targetId],
  );

  const activeTargetId = useRef<string | null>(null);
  activeTargetId.current = activeTarget?.id ?? null;
  const activeTerminal = useCallback(
    () =>
      activeTargetId.current
        ? (terminalRefs.current.get(activeTargetId.current) ?? null)
        : null,
    [],
  );

  // Read in a callback that must not be rebuilt when either flips, or every
  // transcript in the session re-renders on a tab change.
  const unseen = useRef(false);
  unseen.current = outOfSight || !active;

  const wasRunning = useRef(new Map<string, boolean>());
  const setTargetRunning = useCallback(
    (id: string, running: boolean, line?: string) => {
      const finished = wasRunning.current.get(id) && !running;
      wasRunning.current.set(id, running);
      if (finished && unseen.current) noteCliCommandDone(clusterName);
      noteCliRunning(clusterName, id, running ? (line ?? '') : null);

      setRunningTargets(prev => {
        if (prev.has(id) === running) return prev;
        const next = new Set(prev);
        if (running) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [clusterName],
  );

  const showTarget = (id: string) => {
    setTargetId(id);
    setTimeout(() => terminalRefs.current.get(id)?.focus(), 0);
  };

  // Ctrl-` toggles focus into the terminal, as VS Code and Cloud Shell do. Only the session on
  // screen listens, and it unfolds the dock first — the shortcut is how you get back to a folded one.
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '`' || !event.ctrlKey) return;
      event.preventDefault();
      setCliDockMinimized(false);
      setTimeout(() => activeTerminal()?.focus(), 0);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, activeTerminal]);

  const insertLine = useCallback(
    (line: string) => {
      setTimeout(() => activeTerminal()?.insertLine(line), 0);
    },
    [activeTerminal],
  );

  const terminalsMounted =
    Boolean(cli.session) &&
    !cli.isConnecting &&
    !cli.isCommandsLoading &&
    !cli.isTargetsLoading &&
    cli.targets.length > 0;

  useEffect(() => {
    if (!request || !terminalsMounted) return;
    takeCliRequest(request.seq);
    const wanted = request.targetId
      ? cli.targets.find(target => target.id === request.targetId)
      : activeTarget;
    const target = wanted ?? activeTarget;
    if (!target) return;
    setTargetId(target.id);
    setTimeout(() => {
      const terminal = terminalRefs.current.get(target.id);
      if (!terminal) return;
      if (!wanted)
        terminal.note(
          request.line
            ? `${request.targetId} is not one of this session's pods, so the line waits in this prompt instead.`
            : `${request.targetId} is not one of this session's pods.`,
        );
      if (!request.line) terminal.focus();
      else if (request.run && wanted) terminal.execute(request.line);
      else terminal.insertLine(request.line);
    }, 0);
  }, [request, terminalsMounted, cli.targets, activeTarget]);

  const mapHref = activeTarget
    ? `${clusterInfraPath(clusterName)}?${new URLSearchParams({
        path: serializePath(mapPathOfTarget(clusterName, activeTarget)),
      })}`
    : null;

  const showOnMap = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !mapHref ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    const [pathname, search] = mapHref.split('?');
    if (location.pathname === pathname) {
      const params = new URLSearchParams(location.search);
      params.set('path', new URLSearchParams(search).get('path') ?? '');
      navigate({ search: `?${params}` }, { replace: true });
      return;
    }
    const from = `${location.pathname}${location.search}`;
    startPageTransition({
      kind: 'open-map',
      origin: centreOf(event.currentTarget),
      update: () => navigate(mapHref, { state: { from } }),
    });
  };

  // Opening a tool puts it in the side slot, which holds exactly one, so whatever
  // was there steps aside — a tool already taken outside is left where it is.
  const toggleTool = (key: CliToolKey) => {
    setTools(prev => {
      if (prev[key] !== 'closed') return { ...prev, [key]: 'closed' };
      const next = { ...prev, [key]: 'docked' as ToolMode };
      const displaced = dockedIn(prev);
      if (displaced && displaced !== key) next[displaced] = 'closed';
      return next;
    });
  };

  // A tool dropped back on the dock takes the side slot, so the one sitting there goes.
  const redock = (key: CliToolKey) =>
    setTools(prev => {
      const displaced = dockedIn(prev);
      const next = { ...prev, [key]: 'docked' as ToolMode };
      if (displaced && displaced !== key) next[displaced] = 'closed';
      return next;
    });

  const setToolMode = (key: CliToolKey, mode: ToolMode) =>
    setTools(prev => ({ ...prev, [key]: mode }));

  const dockRect = useCallback(
    () => bodyRef.current?.getBoundingClientRect() ?? null,
    [],
  );

  const toolTabs = (
    <div className="CliDock__tools">
      {TOOLS.map(({ key, label, icon }) => {
        const open = tools[key] !== 'closed';
        return (
          <button
            key={key}
            type="button"
            aria-pressed={open}
            className={`CliDock__tool${open ? ' is-active' : ''}`}
            onClick={() => toggleTool(key)}
          >
            {icon}
            <span>{label}</span>
            {open && (
              <motion.span
                layoutId={`cli-tool-${clusterName}`}
                className="CliDock__tool-rule"
                transition={TARGET_SPRING}
              />
            )}
          </button>
        );
      })}
      {canOpenMap && mapHref && activeTarget && (
        <Tooltip title={`Show ${activeTarget.label} on the map`}>
          <a
            className="CliDock__tool CliDock__tool--map"
            href={mapHref}
            aria-label={`Show ${activeTarget.label} on the map`}
            onClick={showOnMap}
          >
            <InfraGraphIcon />
          </a>
        </Tooltip>
      )}
    </div>
  );

  if (cli.isConnecting || cli.isCommandsLoading || cli.isTargetsLoading) {
    return (
      <div className="CliDock__state">
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  if (cli.connectionError || !cli.session) {
    const failure = cli.connectionError as AxiosError<{
      code?: string;
      error?: string;
    }> | null;
    const upstream = failure?.response?.data;
    // The token was never pasted, was revoked in the Portal, or has expired: retrying cannot
    // recover it, so the way out is the picker, which asks for a new one.
    const needsToken =
      upstream?.code === 'CLI_TOKEN_REQUIRED' ||
      upstream?.code === 'CLI_TOKEN_REJECTED';

    return (
      <div className="CliDock__state">
        <Alert
          type={needsToken ? 'warning' : 'error'}
          showIcon
          message={`Could not open a CLI session on ${clusterName}`}
          description={upstream?.error ?? failure?.message}
          action={
            needsToken ? (
              <Button
                size="small"
                icon={<KeyOutlined />}
                onClick={() => setCliPicking(true, clusterName)}
              >
                Enter a token
              </Button>
            ) : (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => cli.reconnect()}
              >
                Retry
              </Button>
            )
          }
        />
      </div>
    );
  }

  return (
    <div
      className={`CliDock__session-body${dropping ? ' is-drop-target' : ''}`}
      ref={bodyRef}
    >
      <div className="CliDock__panel CliDock__panel--terminal">
        <div className="CliDock__targetbar">
          <div className="CliDock__targets" role="tablist" aria-label="Pods">
            {cli.targets.map(target => {
              const isActive = target.id === activeTarget?.id;
              return (
                <Tooltip
                  key={target.id}
                  title={describeTargetTab(target)}
                  mouseEnterDelay={0.6}
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`CliDock__target${isActive ? ' is-active' : ''}${runningTargets.has(target.id) ? ' is-running' : ''}`}
                    onClick={() => showTarget(target.id)}
                  >
                    <span className="CliDock__target-label">
                      {target.label}
                    </span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
          {toolTabs}
        </div>

        <div className="CliDock__terminals">
          {cli.targets.map((target, index) => (
            <div
              key={`${clusterName}:${target.id}`}
              className="CliDock__terminal"
              hidden={target.id !== activeTarget?.id}
            >
              <CliTerminal
                ref={handle => {
                  if (handle) terminalRefs.current.set(target.id, handle);
                  else terminalRefs.current.delete(target.id);
                }}
                clusterName={clusterName}
                target={target}
                showIntro={index === 0}
                commands={cli.availableCommands}
                catalogSource={cli.catalogSource}
                targets={cli.targets}
                run={cli.run}
                upload={cli.upload}
                saveEdit={cli.saveEdit}
                cancel={cli.cancel}
                reconnect={cli.reconnect}
                onRunningChange={(running, line) =>
                  setTargetRunning(target.id, running, line)
                }
              />
            </div>
          ))}
        </div>
      </div>

      {dockedIn(tools) && <div className="CliDock__side" ref={setSideHost} />}

      {TOOLS.map(({ key, label, icon }) =>
        tools[key] === 'closed' ? null : (
          <CliToolPanel
            key={key}
            title={label}
            icon={icon}
            docked={tools[key] === 'docked'}
            visible={active}
            sideHost={sideHost}
            dockRect={dockRect}
            onFloat={() => setToolMode(key, 'floating')}
            onDock={() => redock(key)}
            onOverDock={setDropping}
            onClose={() => setToolMode(key, 'closed')}
          >
            <CliCommandPalette
              mode={key}
              commands={cli.availableCommands}
              recipes={cli.recipes}
              onInsert={insertLine}
            />
          </CliToolPanel>
        ),
      )}
    </div>
  );
}

const describeTargetTab = (target: CliTarget) => {
  if (target.kind === 'self-managed') {
    return `Self-managed ${target.subClusterType ?? 'sub-cluster'} -- ic ... --self-managed-name ${target.self_managed_name} --node-no ${target.node_number ?? 0}`;
  }
  const number =
    target.node_number === undefined ? '' : ` --node-no ${target.node_number}`;
  return `ic ... --node ${target.node}${number}`;
};

// Every dock-level change -- a drag on its edge, another tab's news -- would otherwise re-render
// every transcript in every session.
export default memo(CliSessionPanel);
